import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { REPAIR_STATUSES } from '../../utils/statuses';
import { fmt, fmtDt, money, errMsg } from '../../utils/helpers';
import { Badge, Spinner, EmptyState, Tabs, Select, Modal, ConfirmModal, Field } from '../../components/common/UI';
import { CommentsPanel, FilesPanel, ActivityLog } from '../../components/common/Panels';
import { PaymentModal } from '../../components/common/PaymentModal';
import { NewRepairModal } from '../NewEntityModals';
import { ArrowLeft, Plus, RefreshCw, Search, Save, Edit2, X, Shield, Wrench, ClipboardList, CheckCircle, Trash2, Package } from 'lucide-react';
import toast from 'react-hot-toast';

// Производитель меняет вручную только эти статусы
const STATUS_OPTS = Object.entries(REPAIR_STATUSES)
  .filter(([k]) => !['returned'].includes(k))
  .map(([v, d]) => ({ value: v, label: d.label }));

const RESULT_STATUS_OPTS = [
  { value: 'impossible',   label: 'Ремонт невозможен' },
  { value: 'not_feasible', label: 'Нецелесообразен (высокая стоимость)' },
  { value: 'partial',      label: 'Частично восстановлено' },
  { value: 'full',         label: 'Полностью восстановлено' },
];


const REPAIR_DEVICE_TYPE_META = {
  catalog_product: {
    label: 'Типовое устройство',
    icon: ClipboardList,
    className: 'bg-blue-950/60 text-blue-300 border-blue-800/80',
  },
  catalog_kit: {
    label: 'Устройство в комплекте',
    icon: Package,
    className: 'bg-violet-950/60 text-violet-300 border-violet-800/80',
  },
  external: {
    label: 'Чужой товар',
    icon: Wrench,
    className: 'bg-amber-950/60 text-amber-300 border-amber-800/80',
  },
};

function RepairDeviceTypeMark({ type }) {
  const meta = REPAIR_DEVICE_TYPE_META[type] || REPAIR_DEVICE_TYPE_META.external;
  const Icon = meta.icon;
  return (
    <span
      className={`inline-flex items-center justify-center h-7 w-7 rounded-lg border ${meta.className}`}
      title={meta.label}
      aria-label={meta.label}
    >
      <Icon size={14} />
    </span>
  );
}
const RESULT_STATUS_COLORS = {
  impossible:   'bg-red-900/50 text-red-300 border-red-700',
  not_feasible: 'bg-orange-900/50 text-orange-300 border-orange-700',
  partial:      'bg-yellow-900/50 text-yellow-300 border-yellow-700',
  full:         'bg-green-900/50 text-green-300 border-green-700',
};

// ── СПИСОК ────────────────────────────────────────────────────
export function RepairsPage() {
  const { user, isProducer, isDealerAdmin, hasPerm } = useAuth();
  const navigate = useNavigate();
  const [repairs, setRepairs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [page, setPage] = useState(1);
  const LIMIT = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/repairs', { params: { search, status, page, limit: LIMIT } });
      setRepairs(data.data);
      setTotal(data.total);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setLoading(false); }
  }, [search, status, page]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-gray-100">Ремонт</h1>
          <p className="text-xs text-gray-600 mt-0.5">{total} записей</p>
        </div>
        <div className="flex gap-2">
          <button className="btn-ghost p-2" onClick={load}><RefreshCw size={15} className={loading ? 'animate-spin' : ''} /></button>
          {hasPerm('repairs', 'create') && <button className="btn-primary" onClick={() => setShowNew(true)}><Plus size={15} /> Новый ремонт</button>}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-48">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500" />
          <input className="input pl-8" placeholder="Серийный номер, название..."
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="w-44">
          <Select value={status} onChange={setStatus}
            options={[{ value: '', label: 'Все активные' }, ...STATUS_OPTS]} />
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {Object.entries(REPAIR_STATUSES).map(([k, v]) => (
          <button key={k} onClick={() => setStatus(status === k ? '' : k)}
            className={'badge border cursor-pointer transition-all ' + (status === k ? v.color : 'bg-transparent text-gray-500 border-gray-700 hover:border-gray-500')}>
            {v.label}
          </button>
        ))}
      </div>

      <div className="card overflow-hidden">
        {loading && !repairs.length
          ? <div className="flex justify-center py-12"><Spinner size={24} /></div>
          : !repairs.length ? <EmptyState text="Нет записей о ремонте" />
          : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-800">
                    <th className="text-left px-4 py-3 text-xs text-gray-500 font-medium w-28">Номер</th>
                    <th className="text-left px-4 py-3 text-xs text-gray-500 font-medium">Устройство</th>
                    <th className="text-left px-4 py-3 text-xs text-gray-500 font-medium w-32">С/н</th>
                    {isProducer && <th className="text-left px-4 py-3 text-xs text-gray-500 font-medium">Дилер</th>}
                    <th className="text-left px-4 py-3 text-xs text-gray-500 font-medium w-32">Статус</th>
                    <th className="text-left px-4 py-3 text-xs text-gray-500 font-medium w-20">Гарантия</th>
                    <th className="text-left px-4 py-3 text-xs text-gray-500 font-medium w-28">Принят</th>
                  </tr>
                </thead>
                <tbody>
                  {repairs.map(r => {
                    const st = REPAIR_STATUSES[r.status] || {};
                    return (
                      <tr key={r.id} className="table-row" onClick={() => navigate('/repairs/' + r.id)}>
                        <td className="px-4 py-3"><span className="mono text-blue-400 text-xs">{r.repair_number}</span></td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <RepairDeviceTypeMark type={r.device_type} />
                            <div className="min-w-0">
                              <div className="text-gray-200 font-medium truncate">{r.item_name}</div>
                              <div className="text-[11px] text-gray-500 mt-0.5">{(REPAIR_DEVICE_TYPE_META[r.device_type] || REPAIR_DEVICE_TYPE_META.external).label}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-500 mono text-xs">{r.serial_number || '—'}</td>
                        {isProducer && <td className="px-4 py-3 text-gray-400">{r.dealer_name}</td>}
                        <td className="px-4 py-3">
                          <Badge className={st.color}>{st.label}</Badge>
                          {r.status === 'pending' && !isProducer && (
                            <div className="text-xs text-gray-600 mt-0.5">Ожидает отправки к производителю</div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {r.is_warranty
                            ? <span className="text-green-400 text-xs flex items-center gap-1"><Shield size={11} /> Да</span>
                            : <span className="text-gray-600 text-xs">Нет</span>}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-600">{fmt(r.received_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
      </div>
      {total > LIMIT && (
        <div className="flex items-center justify-center gap-2">
          <button className="btn-secondary" disabled={page === 1} onClick={() => setPage(p => p - 1)}>←</button>
          <span className="text-sm text-gray-500">стр. {page} из {Math.ceil(total / LIMIT)}</span>
          <button className="btn-secondary" disabled={page * LIMIT >= total} onClick={() => setPage(p => p + 1)}>→</button>
        </div>
      )}

      {showNew && <NewRepairModal onClose={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

// ── КАРТОЧКА РЕМОНТА ──────────────────────────────────────────
export function RepairDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isProducer, isDealerAdmin, hasPerm } = useAuth();

  const [repair, setRepair] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('info');
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    device_type: 'external', product_id: '', item_name: '', serial_number: '',
    symptoms: '', is_warranty: false, end_user_info: '', sale_date: '',
  });
  const [kitLines, setKitLines] = useState([]);
  const [saving, setSaving] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCat, setSelectedCat] = useState('');

  const [statusModal, setStatusModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deletePaymentConfirm, setDeletePaymentConfirm] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const [paymentModal, setPaymentModal] = useState(false);

  const [diagModal, setDiagModal] = useState(false);
  const [diagForm, setDiagForm] = useState({ symptoms: '', cause: '', proposed_work: '', estimated_cost: '', payer_company_id: '', receiver_company_id: '' });

  const [compModal, setCompModal] = useState(false);
  const [returnModal, setReturnModal] = useState(false);
  const [returnComment, setReturnComment] = useState('');
  const [compForm, setCompForm] = useState({ work_done: '', final_cost: '', result_status: 'full', payer_company_id: '', receiver_company_id: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/repairs/' + id);
      setRepair(data);
      setForm({
        device_type: data.device_type || 'external',
        product_id: data.product_id || '',
        item_name: data.item_name,
        serial_number: data.serial_number || '',
        symptoms: data.symptoms || '',
        is_warranty: data.is_warranty,
        end_user_info: data.end_user_info || '',
        sale_date: data.sale_date || '',
      });
      setKitLines((data.kit_lines || []).map(l => ({ ...l, category_id: '' })));
      setNewStatus(data.status);
      if (data.diagnosis) {
        setDiagForm({
          symptoms: data.diagnosis.symptoms || '',
          cause: data.diagnosis.cause || '',
          proposed_work: data.diagnosis.proposed_work || '',
          estimated_cost: data.diagnosis.estimated_cost || '',
          payer_company_id: data.diagnosis.payer_company_id || '',
          receiver_company_id: data.diagnosis.receiver_company_id || '',
        });
      }
      if (data.completion) {
        setCompForm({
          work_done: data.completion.work_done || '',
          final_cost: data.completion.final_cost || '',
          result_status: data.completion.result_status || 'full',
          payer_company_id: data.completion.payer_company_id || '',
          receiver_company_id: data.completion.receiver_company_id || '',
        });
      }
    } catch { navigate('/repairs'); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Загружаем компании сразу при открытии карточки (нужны для актов и платежей)
  useEffect(() => {
    api.get('/admin/companies').then(({ data }) => setCompanies(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!editing) return;
    Promise.all([
      api.get('/catalog/products', { params: { active_only: 'true' } }),
      api.get('/catalog/categories'),
    ]).then(([pr, ca]) => {
      setProducts(pr.data);
      setCategories(ca.data);
    });
  }, [editing]);

  useEffect(() => {
    if (!products.length) return;
    setKitLines(ls => ls.map(line => {
      if (line.category_id || !line.product_id) return line;
      const prod = products.find(p => p.id === +line.product_id);
      return prod ? { ...line, category_id: prod.category_id || '' } : line;
    }));
  }, [products]);

  const myCompanies = companies.filter(c => !c.dealer_id).map(c => ({ value: c.id, label: c.name }));
  const dealerCompanies = companies.filter(c => repair?.dealer_id ? c.dealer_id === repair.dealer_id : c.dealer_id)
    .map(c => ({ value: c.id, label: c.name }));

  const catOpts = categories.map(c => ({ value: c.id, label: c.name }));
  const filteredProducts = selectedCat ? products.filter(p => p.category_id === +selectedCat) : products;
  const productOpts = filteredProducts.map(p => ({ value: p.id, label: p.name + (p.sku ? ' [' + p.sku + ']' : '') }));

  const selectProduct = (id) => {
    const prod = products.find(p => p.id === +id);
    setForm(f => ({ ...f, product_id: id, item_name: prod?.name || '' }));
  };

  const addKitLine = () => {
    setKitLines(ls => [...ls, { _tempId: Date.now(), category_id: '', product_id: '', product_name: '', quantity: 1 }]);
  };

  const updateKitLine = (idx, patch) => {
    setKitLines(ls => ls.map((l, i) => i === idx ? { ...l, ...patch } : l));
  };

  const selectKitLineCategory = (idx, categoryId) => {
    updateKitLine(idx, { category_id: categoryId, product_id: '', product_name: '' });
  };

  const selectKitLineProduct = (idx, pid) => {
    const prod = products.find(p => p.id === +pid);
    updateKitLine(idx, {
      product_id: pid,
      product_name: prod?.name || '',
      category_id: prod?.category_id || kitLines[idx]?.category_id || '',
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch('/repairs/' + id, {
        ...form,
        product_id: form.device_type === 'catalog_product' ? form.product_id : '',
        kit_lines: form.device_type === 'catalog_kit'
          ? kitLines.map((l, i) => ({ product_id: l.product_id, quantity: l.quantity || 1, sort_order: i }))
          : undefined,
      });
      toast.success('Сохранено');
      setEditing(false);
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const saveDiagnosis = async () => {
    setSaving(true);
    try {
      await api.put('/repairs/' + id + '/diagnosis', diagForm);
      toast.success('Акт диагностики сохранён');
      setDiagModal(false);
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const saveCompletion = async () => {
    setSaving(true);
    try {
      await api.put('/repairs/' + id + '/completion', compForm);
      toast.success('Акт выполненных работ сохранён');
      setCompModal(false);
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const deletePayment = async (pid) => {
    try {
      await api.delete('/repairs/' + id + '/payments/' + pid);
      toast.success('Платёж удалён'); load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const deleteRepair = async () => {
    try {
      await api.delete('/repairs/' + id);
      toast.success('Ремонт удалён');
      navigate('/repairs');
    } catch (err) { toast.error(errMsg(err)); }
  };

  const changeStatus = async () => {
    try {
      await api.patch('/repairs/' + id + '/status', { status: newStatus, note: statusNote });
      toast.success('Статус изменён');
      setStatusModal(false);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  if (loading) return <div className="flex justify-center items-center h-64"><Spinner size={32} /></div>;

  const st = repair ? REPAIR_STATUSES[repair.status] : null;
  const canSeePrices = hasPerm('repairs', 'view_prices');
  const showPaymentDetails = (isProducer || isDealerAdmin) && canSeePrices;
  const canEditRepair = isProducer && hasPerm('repairs', 'edit');
  const canChangeRepairStatus = isProducer && hasPerm('repairs', 'edit_status');
  const canAddRepairPayment = isProducer && hasPerm('repairs', 'add_payment');
  const canDeleteRepair = user?.role === 'superadmin';
  const totalPaid = showPaymentDetails ? (repair?.payments?.reduce((s, p) => s + +p.amount, 0) || 0) : 0;
  const diagnosis = repair?.diagnosis;
  const completion = repair?.completion;

  const TABS = [
    { id: 'info',      label: 'Устройство' },
    { id: 'diagnosis', label: 'Диагностика' },
    { id: 'completion',label: 'Акт работ' },
    ...(showPaymentDetails ? [{ id: 'payments', label: 'Оплата', count: repair?.payments?.length }] : []),
    { id: 'comments',  label: 'Комментарии',  count: repair?.comments?.length },
    { id: 'files',     label: 'Файлы',        count: repair?.files?.length },
    { id: 'history',   label: 'История' },
  ];

  const DeviceForm = () => (
    <div className="space-y-4">
      <Field label="Тип устройства" required>
        <div className="flex gap-3">
          {[
            { value: 'catalog_product', label: 'Типовое устройство', icon: '📦' },
            { value: 'catalog_kit',     label: 'Устройство в комплекте', icon: '🗂' },
            { value: 'external',        label: 'Чужое оборудование', icon: '🔧' },
          ].map(opt => (
            <label key={opt.value} className="flex-1 flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors
              border-gray-700 hover:border-gray-500"
              style={form.device_type === opt.value ? { borderColor: '#3b82f6', background: 'rgba(59,130,246,0.1)' } : {}}>
              <input type="radio" name="device_type" value={opt.value}
                checked={form.device_type === opt.value}
                onChange={e => {
                  const nextType = e.target.value;
                  setForm(f => ({ ...f, device_type: nextType, product_id: '', item_name: '' }));
                  if (nextType !== 'catalog_kit') setKitLines([]);
                }}
                className="accent-blue-500" />
              <span className="text-sm">{opt.icon} {opt.label}</span>
            </label>
          ))}
        </div>
      </Field>

      {form.device_type === 'catalog_product' && (
        <div className="grid grid-cols-2 gap-3">
          <Field label="Категория">
            <Select value={selectedCat} onChange={v => { setSelectedCat(v); setForm(f => ({ ...f, product_id: '', item_name: '' })); }}
              options={catOpts} placeholder="Все категории" />
          </Field>
          <Field label="Типовое устройство" required>
            <Select value={form.product_id} onChange={selectProduct}
              options={productOpts} placeholder="Выберите товар" />
          </Field>
        </div>
      )}

      <Field label={form.device_type === 'catalog_kit' ? 'Название комплекта' : form.device_type === 'external' ? 'Название устройства' : 'Наименование'} required>
        <input className="input" value={form.item_name}
          onChange={e => setForm(f => ({ ...f, item_name: e.target.value }))}
          placeholder={form.device_type === 'catalog_kit'
            ? 'Например: Комплект автоматики ворот'
            : form.device_type === 'external'
              ? 'Марка, модель устройства'
              : 'Автозаполняется при выборе товара'} />
      </Field>

      {form.device_type === 'catalog_kit' && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="label">Состав комплекта</span>
            <button className="btn-secondary text-xs" onClick={addKitLine}><Plus size={12} /> Добавить</button>
          </div>
          <p className="text-xs text-gray-500 mb-2">Новый комплект собирается в ремонте так же, как и в заказе: произвольное название сверху, ниже товары из справочника по категориям.</p>
          {kitLines.length === 0
            ? <p className="text-xs text-gray-600 text-center py-4 border border-dashed border-gray-700 rounded-lg">Добавьте товары из справочника. Категории можно смешивать, количество задаётся на комплект</p>
            : kitLines.map((line, idx) => {
              const lineProducts = line.category_id ? products.filter(p => p.category_id === +line.category_id) : products;
              const lineOptions = lineProducts.map(p => ({ value: p.id, label: p.name + (p.sku ? ' [' + p.sku + ']' : '') }));
              return (
                <div key={line.id || line._tempId || idx} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_96px_36px] gap-2 items-center mb-2">
                  <Select value={line.category_id} onChange={v => selectKitLineCategory(idx, v)}
                    options={catOpts} placeholder="Категория" />
                  <Select value={line.product_id} onChange={v => selectKitLineProduct(idx, v)}
                    options={lineOptions} placeholder="Устройство в комплекте" />
                  <input type="number" min="1" step="1" className="input text-center"
                    value={line.quantity} onChange={e => updateKitLine(idx, { quantity: e.target.value })} />
                  <button className="btn-ghost p-1 hover:text-red-400"
                    onClick={() => setKitLines(ls => ls.filter((_, i) => i !== idx))}>
                    <X size={14} />
                  </button>
                </div>
              );
            })
          }
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Field label="Серийный номер">
          <input className="input mono" value={form.serial_number}
            onChange={e => setForm(f => ({ ...f, serial_number: e.target.value }))} />
        </Field>
        <Field label="Дата продажи" hint="Для оценки срока эксплуатации">
          <input type="date" className="input" value={form.sale_date}
            onChange={e => setForm(f => ({ ...f, sale_date: e.target.value }))} />
        </Field>
        <div className="flex items-center pt-5">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" className="accent-green-500 w-4 h-4"
              checked={form.is_warranty} onChange={e => setForm(f => ({ ...f, is_warranty: e.target.checked }))} />
            <span className="text-sm text-gray-300">Гарантийный случай</span>
          </label>
        </div>
      </div>

      <Field label="Симптомы (со слов пользователя)">
        <textarea className="input resize-none" rows={3} value={form.symptoms}
          onChange={e => setForm(f => ({ ...f, symptoms: e.target.value }))}
          placeholder="Что сообщил пользователь при сдаче" />
      </Field>

      <Field label="Конечный пользователь / Клиент" hint="Компания, контактные лица, телефоны">
        <textarea className="input resize-none" rows={4} value={form.end_user_info}
          onChange={e => setForm(f => ({ ...f, end_user_info: e.target.value }))}
          placeholder="Название компании, ФИО, телефон, email..." />
      </Field>
    </div>
  );

  return (
    <div className="p-4 md:p-6 max-w-5xl">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-3">
          <button className="btn-ghost p-2" onClick={() => navigate('/repairs')}><ArrowLeft size={16} /></button>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="mono text-blue-400 text-sm">{repair?.repair_number}</span>
              {st && <Badge className={st.color}>{st.label}</Badge>}
              {repair?.is_warranty && <Badge className="bg-green-900/50 text-green-300 border-green-700"><Shield size={10} className="inline mr-1" />Гарантия</Badge>}
              {completion && <Badge className={RESULT_STATUS_COLORS[completion.result_status]}>{RESULT_STATUS_OPTS.find(r => r.value === completion.result_status)?.label}</Badge>}
            </div>
            <h1 className="text-lg font-bold text-gray-100 mt-0.5">{repair?.item_name}</h1>
            {repair?.serial_number && <div className="text-xs mono text-gray-500">С/н: {repair.serial_number}</div>}
          </div>
        </div>
        <div className="flex gap-2">
          {canChangeRepairStatus && <button className="btn-secondary" onClick={() => { setNewStatus(repair.status); setStatusModal(true); }}>Статус</button>}
          {!isProducer && repair?.status === 'shipped' && (
            <button className="btn-primary" onClick={() => setReturnModal(true)}>
              <CheckCircle size={14} /> Подтвердить получение
            </button>
          )}
          {canEditRepair && !editing && <button className="btn-secondary" onClick={() => setEditing(true)}><Edit2 size={14} /></button>}
          {editing && <button className="btn-ghost" onClick={() => setEditing(false)}><X size={14} /></button>}
          {editing && <button className="btn-primary" onClick={save} disabled={saving}>{saving ? <Spinner size={14} /> : <><Save size={14} /> Сохранить</>}</button>}
          {canDeleteRepair && !editing && <button className="btn-danger" onClick={() => setDeleteConfirm(true)}><Trash2 size={14} /> Удалить</button>}
        </div>
      </div>

      {/* Tabs */}
      <div className="card overflow-hidden">
        <Tabs tabs={TABS} active={tab} onChange={setTab} />
        <div className="p-5">

          {/* ── УСТРОЙСТВО ── */}
          {tab === 'info' && (
            editing ? <DeviceForm /> : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-2 space-y-4">
                  <div>
                    <div className="section-title mb-1">Тип</div>
                    <div className="text-sm text-gray-300">
                      {repair?.device_type === 'catalog_product' ? '📦 Типовое устройство' :
                       repair?.device_type === 'catalog_kit'     ? '🗂 Устройство в комплекте' : '🔧 Чужое оборудование'}
                    </div>
                  </div>
                  {repair?.kit_lines?.length > 0 && (
                    <div>
                      <div className="section-title mb-2">Состав</div>
                      <div className="space-y-1">
                        {repair.kit_lines.map(l => (
                          <div key={l.id} className="flex items-center gap-2 text-sm text-gray-400">
                            <span className="text-gray-600">↳</span>
                            <span>{l.product_name}</span>
                            <span className="text-gray-600">× {l.quantity}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {repair?.symptoms && (
                    <div>
                      <div className="section-title mb-1">Симптомы (со слов клиента)</div>
                      <p className="text-sm text-gray-300 whitespace-pre-wrap">{repair.symptoms}</p>
                    </div>
                  )}
                  {repair?.end_user_info && (
                    <div>
                      <div className="section-title mb-1">Конечный пользователь</div>
                      <p className="text-sm text-gray-300 whitespace-pre-wrap">{repair.end_user_info}</p>
                    </div>
                  )}
                  <div>
                    <div className="section-title mb-3">История статусов</div>
                    {repair?.statusLog?.map((s, i) => {
                      const stInfo = REPAIR_STATUSES[s.status] || {};
                      return (
                        <div key={s.id} className="flex gap-3">
                          <div className="flex flex-col items-center">
                            <div className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 shrink-0" />
                            {i < repair.statusLog.length - 1 && <div className="w-px flex-1 bg-gray-800 my-1" />}
                          </div>
                          <div className="pb-3">
                            <Badge className={stInfo.color}>{stInfo.label}</Badge>
                            <div className="text-xs text-gray-500 mt-0.5">{fmtDt(s.changed_at)} · {s.by_name}</div>
                            {s.note && <div className="text-xs text-gray-400 italic mt-0.5">{s.note}</div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="space-y-3 text-sm">
                  {isProducer && <div><div className="section-title mb-1">Дилер</div><div className="text-gray-300">{repair?.dealer_name}</div></div>}
                  <div><div className="section-title mb-1">Принят</div><div className="text-gray-300">{fmt(repair?.received_at)}</div></div>
                  <div><div className="section-title mb-1">Дата продажи</div><div className="text-gray-300">{repair?.sale_date ? fmt(repair.sale_date) : '—'}</div></div>
                  <div><div className="section-title mb-1">Серийный номер</div><div className="text-gray-300 mono">{repair?.serial_number || '—'}</div></div>
                </div>
              </div>
            )
          )}

          {/* ── ДИАГНОСТИКА ── */}
          {tab === 'diagnosis' && (
            <div className="space-y-4">
              {canEditRepair && (
                <button className="btn-primary" onClick={() => setDiagModal(true)}>
                  <ClipboardList size={14} /> {diagnosis ? 'Редактировать акт диагностики' : 'Создать акт диагностики'}
                </button>
              )}
              {!diagnosis ? (
                <div className="text-center py-12 text-gray-600">
                  <ClipboardList size={32} className="mx-auto mb-3 opacity-30" />
                  <p>Акт диагностики ещё не создан</p>
                </div>
              ) : (
                <div className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <div className="section-title mb-2">Симптомы (технические)</div>
                      <p className="text-sm text-gray-300 whitespace-pre-wrap">{diagnosis.symptoms || '—'}</p>
                    </div>
                    <div>
                      <div className="section-title mb-2">Причина неисправности</div>
                      <p className="text-sm text-gray-300 whitespace-pre-wrap">{diagnosis.cause || '—'}</p>
                    </div>
                    <div className="md:col-span-2">
                      <div className="section-title mb-2">Предполагаемые работы</div>
                      <p className="text-sm text-gray-300 whitespace-pre-wrap">{diagnosis.proposed_work || '—'}</p>
                    </div>
                  </div>
                  {showPaymentDetails && (
                    <div className={'grid gap-4 p-4 bg-gray-800/40 rounded-lg ' + (canSeePrices ? 'grid-cols-3' : 'grid-cols-2')}>
                      {canSeePrices && <div><div className="section-title mb-1">Стоимость (оценка)</div><div className="mono text-gray-200">{money(diagnosis.estimated_cost)}</div></div>}
                      <div><div className="section-title mb-1">Плательщик</div><div className="text-gray-300">{diagnosis.payer_name || '—'}</div></div>
                      <div><div className="section-title mb-1">Получатель</div><div className="text-gray-300">{diagnosis.receiver_name || '—'}</div></div>
                    </div>
                  )}
                  <div className="text-xs text-gray-600">Составил: {diagnosis.by_name} · {fmtDt(diagnosis.created_at)}</div>
                </div>
              )}
            </div>
          )}

          {/* ── АКТ ВЫПОЛНЕННЫХ РАБОТ ── */}
          {tab === 'completion' && (
            <div className="space-y-4">
              {canEditRepair && (
                <button className="btn-primary" onClick={() => setCompModal(true)}>
                  <CheckCircle size={14} /> {completion ? 'Редактировать акт работ' : 'Создать акт выполненных работ'}
                </button>
              )}
              {!completion ? (
                <div className="text-center py-12 text-gray-600">
                  <CheckCircle size={32} className="mx-auto mb-3 opacity-30" />
                  <p>Акт выполненных работ ещё не создан</p>
                </div>
              ) : (
                <div className="space-y-5">
                  <div>
                    <div className="section-title mb-2">Выполненные работы</div>
                    <p className="text-sm text-gray-300 whitespace-pre-wrap">{completion.work_done || '—'}</p>
                  </div>
                  <div className="grid gap-4 p-4 bg-gray-800/40 rounded-lg grid-cols-1 md:grid-cols-2">
                    <div>
                      <div className="section-title mb-1">Результат</div>
                      <Badge className={RESULT_STATUS_COLORS[completion.result_status]}>
                        {RESULT_STATUS_OPTS.find(r => r.value === completion.result_status)?.label}
                      </Badge>
                    </div>
                    {showPaymentDetails && (
                      <>
                        <div><div className="section-title mb-1">Итоговая стоимость</div><div className="mono text-gray-200">{money(completion.final_cost)}</div></div>
                        <div><div className="section-title mb-1">Плательщик</div><div className="text-gray-300">{completion.payer_name || '—'}</div></div>
                        <div><div className="section-title mb-1">Получатель</div><div className="text-gray-300">{completion.receiver_name || '—'}</div></div>
                      </>
                    )}
                  </div>
                  {showPaymentDetails && <div className="flex justify-between text-sm border-t border-gray-800 pt-3"><span className="text-gray-500">Оплачено:</span><span className={'mono ' + (totalPaid >= (completion.final_cost || 0) ? 'text-green-400' : 'text-yellow-400')}>{money(totalPaid)}</span></div>}
                  <div className="text-xs text-gray-600">Составил: {completion.by_name} · {fmtDt(completion.created_at)}</div>
                </div>
              )}
            </div>
          )}

          {/* ── ОПЛАТА ── */}
          {tab === 'payments' && showPaymentDetails && (
            <div className="space-y-4">
              {canAddRepairPayment && (
                <button className="btn-primary" onClick={() => setPaymentModal(true)}><Plus size={14} /> Добавить платёж</button>
              )}
              {!repair?.payments?.length && <p className="text-sm text-gray-600">Нет платежей</p>}
              {repair?.payments?.map(p => (
                <div key={p.id} className="flex items-center justify-between p-3 bg-gray-800/60 border border-gray-800 rounded-lg">
                  <div>
                    <div className="text-sm font-medium text-gray-200">{money(p.amount)}</div>
                    <div className="text-xs text-gray-500">{p.payer_name} → {p.receiver_name}</div>
                    {p.doc_number && <div className="text-xs text-gray-500 mono">Документ: {p.doc_number}{p.doc_date ? ' от ' + fmt(p.doc_date) : ''}</div>}
                    {p.note && <div className="text-xs text-gray-600 italic">{p.note}</div>}
                  </div>
                  <div className="text-xs text-gray-600 text-right"><div>{fmt(p.payment_date) || '—'}</div><div>{p.by_name}</div></div>
                </div>
              ))}
            </div>
          )}

          {tab === 'comments' && <CommentsPanel entityType="repair" entityId={id} comments={repair?.comments} onRefresh={load} />}
          {tab === 'files' && <FilesPanel entityType="repair" entityId={id} files={repair?.files} onRefresh={load} />}
          {tab === 'history' && <ActivityLog entries={repair?.actLog} />}
        </div>
      </div>

      {/* Модалки */}
      {deleteConfirm && (
        <ConfirmModal message={'Удалить ремонт ' + repair?.repair_number + '? Это действие нельзя отменить.'}
          onConfirm={deleteRepair}
          onClose={() => setDeleteConfirm(false)} />
      )}

      {deletePaymentConfirm && (
        <ConfirmModal message={'Удалить платёж на ' + money(deletePaymentConfirm.amount) + '?'}
          onConfirm={() => { deletePayment(deletePaymentConfirm.id); setDeletePaymentConfirm(null); }}
          onClose={() => setDeletePaymentConfirm(null)} />
      )}

      {/* Модалка подтверждения получения из ремонта */}
      {returnModal && (
        <Modal title="Подтвердить получение из ремонта" onClose={() => setReturnModal(false)} size="sm">
          <div className="space-y-4">
            <div className="p-3 bg-teal-900/20 border border-teal-800 rounded-lg text-sm text-teal-300">
              Подтвердите что оборудование «{repair?.item_name}» получено обратно из ремонта.
            </div>
            <Field label="Комментарий при получении" hint="Состояние оборудования, замечания, вопросы">
              <textarea className="input resize-none" rows={4} value={returnComment}
                onChange={e => setReturnComment(e.target.value)}
                autoFocus
                placeholder="Необязательно, но рекомендуется..." />
            </Field>
            <div className="flex justify-end gap-2 pt-2">
              <button className="btn-secondary" onClick={() => setReturnModal(false)}>Отмена</button>
              <button className="btn-primary" onClick={async () => {
                try {
                  await api.patch('/repairs/' + id + '/status', {
                    status: 'returned',
                    note: 'Дилер подтвердил получение',
                    comment: returnComment,
                  });
                  toast.success('Получение подтверждено');
                  setReturnModal(false);
                  setReturnComment('');
                  load();
                } catch (err) { toast.error(errMsg(err)); }
              }}>
                <CheckCircle size={14} /> Подтвердить
              </button>
            </div>
          </div>
        </Modal>
      )}

      {statusModal && (
        <Modal title="Сменить статус" onClose={() => setStatusModal(false)} size="sm">
          <div className="space-y-4">
            <Field label="Новый статус"><Select value={newStatus} onChange={setNewStatus} options={STATUS_OPTS} /></Field>
            <Field label="Примечание"><input className="input" value={statusNote} onChange={e => setStatusNote(e.target.value)} /></Field>
            <div className="flex justify-end gap-2">
              <button className="btn-secondary" onClick={() => setStatusModal(false)}>Отмена</button>
              <button className="btn-primary" onClick={changeStatus}>Применить</button>
            </div>
          </div>
        </Modal>
      )}

      {paymentModal && (
        <PaymentModal entityType="repair" entityId={id}
          onClose={() => setPaymentModal(false)} onDone={load} />
      )}

      {/* Акт диагностики */}
      {diagModal && (
        <Modal title="Акт диагностики" onClose={() => setDiagModal(false)} size="lg">
          <div className="space-y-4">
            <Field label="Симптомы (технические)">
              <textarea className="input resize-none" rows={3} value={diagForm.symptoms}
                onChange={e => setDiagForm(f => ({ ...f, symptoms: e.target.value }))}
                placeholder="Техническое описание неисправности" />
            </Field>
            <Field label="Причина неисправности">
              <textarea className="input resize-none" rows={3} value={diagForm.cause}
                onChange={e => setDiagForm(f => ({ ...f, cause: e.target.value }))} />
            </Field>
            <Field label="Предполагаемые работы">
              <textarea className="input resize-none" rows={3} value={diagForm.proposed_work}
                onChange={e => setDiagForm(f => ({ ...f, proposed_work: e.target.value }))} />
            </Field>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Стоимость (оценка), ₽">
                <input type="number" className="input" value={diagForm.estimated_cost}
                  onChange={e => setDiagForm(f => ({ ...f, estimated_cost: e.target.value }))} />
              </Field>
              <Field label="Плательщик">
                <Select value={diagForm.payer_company_id}
                  onChange={v => setDiagForm(f => ({ ...f, payer_company_id: v }))}
                  options={dealerCompanies} placeholder="Компания" />
              </Field>
              <Field label="Получатель">
                <Select value={diagForm.receiver_company_id}
                  onChange={v => setDiagForm(f => ({ ...f, receiver_company_id: v }))}
                  options={myCompanies} placeholder="Компания" />
              </Field>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-gray-800">
              <button className="btn-secondary" onClick={() => setDiagModal(false)}>Отмена</button>
              <button className="btn-primary" onClick={saveDiagnosis} disabled={saving}>
                {saving ? <Spinner size={14} /> : 'Сохранить акт'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Акт выполненных работ */}
      {compModal && (
        <Modal title="Акт выполненных работ" onClose={() => setCompModal(false)} size="lg">
          <div className="space-y-4">
            <Field label="Результат ремонта" required>
              <Select value={compForm.result_status} onChange={v => setCompForm(f => ({ ...f, result_status: v }))}
                options={RESULT_STATUS_OPTS} />
            </Field>
            <Field label="Выполненные работы">
              <textarea className="input resize-none" rows={5} value={compForm.work_done}
                onChange={e => setCompForm(f => ({ ...f, work_done: e.target.value }))}
                placeholder="Подробное описание выполненных работ" />
            </Field>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Итоговая стоимость, ₽">
                <input type="number" className="input" value={compForm.final_cost}
                  onChange={e => setCompForm(f => ({ ...f, final_cost: e.target.value }))} />
              </Field>
              <Field label="Плательщик">
                <Select value={compForm.payer_company_id}
                  onChange={v => setCompForm(f => ({ ...f, payer_company_id: v }))}
                  options={dealerCompanies} placeholder="Компания" />
              </Field>
              <Field label="Получатель">
                <Select value={compForm.receiver_company_id}
                  onChange={v => setCompForm(f => ({ ...f, receiver_company_id: v }))}
                  options={myCompanies} placeholder="Компания" />
              </Field>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-gray-800">
              <button className="btn-secondary" onClick={() => setCompModal(false)}>Отмена</button>
              <button className="btn-primary" onClick={saveCompletion} disabled={saving}>
                {saving ? <Spinner size={14} /> : 'Сохранить акт'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
